import type { GameMode } from '../gameMode.types';
import type { ActivityPayloadMap } from './index';

export type ExamIntegrityMode = 'STRICT' | 'FLEXIBLE';

/** HOST = el docente avanza bloques para todos; AUTONOMOUS = cada alumno a su ritmo */
export type ExamAdvanceMode = 'HOST' | 'AUTONOMOUS';

export type ExamResourceType = 'exam' | 'guide';

/** Soporte de entrega — bifurcación Modelo 2 (print vs proyector). */
export type DeliveryFormat = 'print' | 'interactive';

/** Perfil pedagógico — teoría, recordatorios y práctica según intención docente. */
export type DeliveryProfile = 'learning_guide' | 'interactive_class' | 'assessment';

/** Currículum declarado por el docente vs. material libre sin marco MINEDUC. */
export type GuideGenerationMode = 'curricular' | 'independent';

export type GuideLength = 'short' | 'medium' | 'long';

/** Selección explícita OA/OAH/OAA — bypass del RAG por keywords. */
export interface CurriculumTarget {
  axis: string;
  oaCode: string;
  oahCode?: string;
  oaaCode?: string;
}

/** Opciones de generación de guía enviadas al backend (/api/exams/generate con resourceType=guide). */
export interface GuideGenerationRequest {
  /** @deprecated Usar contentContext — se mantiene por compatibilidad. */
  prompt?: string;
  /** Intención docente y contexto de contenido (micro-planificación). */
  contentContext?: string;
  generationMode?: GuideGenerationMode;
  curriculumTarget?: CurriculumTarget;
  level?: string;
  subjectTag?: string;
  extension?: string;
  focus?: string;
  resourceType?: 'guide';
}

export interface CurriculumObjectives {
  eje: string;
  ejeLabel: string;
  /** Nivel escolar declarado al generar (ej. 5° Básico) — impreso como Curso. */
  levelLabel?: string;
  /** Códigos MINEDUC seleccionados en el modal curricular. */
  oaCode?: string;
  oahCode?: string;
  oaaCode?: string;
  /** Meta de clase en lenguaje infantil — lo que ve el estudiante */
  synthesizedGoal: string;
  /** Referencia interna MINEDUC (docente / metadatos) */
  objetivo?: string;
  habilidad?: string;
  actitud?: string;
  objetivos?: string[];
  habilidades?: string[];
}

/** Vínculo a una sesión planificada en la ruta de aprendizaje del curso. */
export interface LinkedSessionRef {
  courseId: string;
  unitId: string;
  sessionId: string;
}

/** Contexto pedagógico completo para generación IA desde una sesión planificada. */
export interface LinkedSessionContext extends LinkedSessionRef {
  unitTitle: string;
  sessionTitle: string;
  sessionObjective: string;
  sessionSummary: string;
  /** Marco tridimensional heredado de la unidad planificada (OA/OAH/OAA). */
  curriculumTarget?: CurriculumTarget;
  /** Modo curricular de la unidad — default curricular si hay target. */
  generationMode?: GuideGenerationMode;
  /** Nivel del curso — resolución MINEDUC en servidor. */
  level?: string;
  /** Tag de asignatura — resolución MINEDUC en servidor. */
  subjectTag?: string;
  /** Dimensión dominante del hito — prioriza en generación de material. */
  curriculumSlice?: {
    dimensionDominante: 'OA' | 'OAH' | 'OAA';
    dimensionCode?: string;
  };
  /** Formato de sesión planificado (Destreza, Indagación, Taller, Evaluación). */
  sessionConfig?: {
    archetype: 'destreza' | 'indagacion' | 'taller' | 'evaluacion';
    variant?: 'guiado' | 'autonomo';
  };
  /** Flujo pedagógico estructurado — intención docente/estudiante por paso. */
  sessionFlow?: LinkedSessionFlowStep[];
  /**
   * Perfil de flujo elegido por el docente (wizard Studio).
   * Prioridad sobre inferencia heurística en SessionIntentContract.
   */
  flowProfile?:
    | 'scale_project'
    | 'measurement_inquiry'
    | 'workshop_scaffold'
    | 'skill_drill'
    | 'source_inquiry'
    | 'generic';
  /** Opt-in: permite profiles de medición fuera de matemática (raro). */
  allowMeasurementDomain?: boolean;
  /** Opt-in: permite source_inquiry fuera de historia (raro). */
  allowSourceInquiryDomain?: boolean;
  /**
   * Ancla visual del docente (override) — URL o wikiQuery exactos.
   * Prioridad sobre referenceImage del Content Pack.
   */
  visualAnchor?: {
    imageUrl?: string;
    wikiQuery?: string;
    note?: string;
  };
}

/** Paso del flujo de sesión — serializable para generación IA. */
export interface LinkedSessionFlowStep {
  title: string;
  durationMin?: number;
  durationLabel?: string;
  teacher?: string;
  students?: string;
  materials?: string;
}

export interface EvaluationExamSettings {
  shuffleBlocks: boolean;
  allowRejoin: boolean;
  integrityMode: ExamIntegrityMode;
  /** Por defecto autónomo — experiencia lineal del estudiante */
  advanceMode: ExamAdvanceMode;
}

/** Bloque polimórfico dentro de una evaluación programada */
export interface EvaluationBlock<M extends GameMode = GameMode> {
  id: string;
  index: number;
  mode: M;
  label?: string;
  payload: ActivityPayloadMap[M];
}

export interface EvaluationExam {
  id: string;
  teacherId: string;
  title: string;
  subjectTag: string;
  /** Guía de aprendizaje formativa */
  resourceType?: ExamResourceType;
  /** Clase proyectable (HOST) vs ficha imprimible — guías usan print implícito. */
  deliveryFormat?: DeliveryFormat;
  /** Capas pedagógicas — guía vs clase vs evaluación. */
  deliveryProfile?: DeliveryProfile;
  /** Atlas / apunte teórico — oculta UI de evaluación (números, Nombre/Fecha) */
  isStudyGuide?: boolean;
  /** Objetivos MINEDUC (OA/OAH/OAA) cuando aplica escudo curricular */
  curriculumObjectives?: CurriculumObjectives;
  createdAt: number;
  updatedAt: number;
  settings: EvaluationExamSettings;
  blocks: EvaluationBlock[];
  /** Código de acceso único (6 chars) — distinto del joinCode de sala en vivo */
  accessCode?: string;
  /** Sala server-side creada al programar evaluación async */
  asyncRoomId?: string;
  /** joinCode interno de la sala async (solo para monitor docente) */
  asyncRoomJoinCode?: string;
  /** Timestamp de publicación en servidor */
  programmedAt?: number;
  /** Curso al que pertenece el material (Studio course-centric). */
  courseId?: string;
  /** Nivel curricular usado al generar / editar con IA (persistido en el dispositivo). */
  level?: string;
  /** Sesión de la ruta de aprendizaje desde la que se generó o vinculó el material. */
  linkedSession?: LinkedSessionRef;
}

export interface StudentExamProgress {
  participantId: string;
  blockIndex: number;
  completedBlocks: number;
  totalBlocks: number;
  locked?: boolean;
  finished?: boolean;
}

export interface ExamPendingReview {
  participantId: string;
  displayName: string;
  blockId: string;
  blockLabel?: string;
  text: string;
  score: number;
  feedback: string;
  pending: boolean;
}

/** Metadatos públicos de evaluación programada (lookup por accessCode) */
export interface ScheduledExamInfo {
  accessCode: string;
  examId: string;
  title: string;
  subjectTag: string;
  teacherId: string;
  programmedAt: number;
  roomId: string;
  advanceMode: ExamAdvanceMode;
  status: 'ACTIVE' | 'ENDED';
  totalBlocks: number;
}

export interface ProgramExamResponse {
  ok: boolean;
  accessCode: string;
  roomId: string;
  joinCode: string;
  examId: string;
  title: string;
  subjectTag: string;
  programmedAt: number;
}

/** Definición del mazo sincronizada en la sala */
export interface ExamDefinition {
  examId: string;
  title: string;
  subjectTag: string;
  blocks: Array<{
    id: string;
    index: number;
    mode: GameMode;
    label?: string;
    payload: unknown;
  }>;
}

export interface ExamSessionState {
  examId: string;
  title: string;
  integrityMode: ExamIntegrityMode;
  advanceMode: ExamAdvanceMode;
  shuffleBlocks?: boolean;
  currentBlockIndex: number;
  totalBlocks: number;
  status: 'ACTIVE' | 'ENDED';
  /** Progreso por alumno — monitor del host */
  progress: Record<string, StudentExamProgress>;
  /** Orden de bloques por alumno (modo autónomo + shuffle) */
  blockOrders?: Record<string, string[]>;
  /** Propuestas IA de bloques desarrollo — revisión docente */
  pendingReviews?: ExamPendingReview[];
  /** Atlas / apunte — lectura continua sin numeración de evaluación */
  isStudyGuide?: boolean;
}

/** Vista del bloque actual para un alumno en modo autónomo */
export interface StudentExamSlot {
  blockIndex: number;
  totalBlocks: number;
  blockId: string;
  blockLabel?: string;
  mode: GameMode;
  payload: unknown;
  runtime: unknown;
  finished?: boolean;
}

/** Modos disponibles como bloques en evaluaciones mixtas */
export const EXAM_BLOCK_MODES: GameMode[] = [
  'info_block',
  'canvas_breakdown',
  'arena',
  'order',
  'point',
  'sandbox',
  'completar',
  'desarrollo',
  'vincula',
  'agrupa',
  'dictado',
  'anatomia',
  'rhythm_canvas',
  'melody_staff',
  'ear_training',
  'symbol_analysis',
  'geometry_canvas',
  'cartesian_plane',
  'math_equation',
  'number_line',
  'counting_collection',
  'data_chart',
  'fraction_model',
  'percent_grid',
];

/** Modos exclusivos de guías teóricas / Atlas (enfoque conceptual-teórico). */
export const STUDY_GUIDE_THEORY_MODES: GameMode[] = [
  'info_block',
  'subtitulo',
  'destacado',
  'figura',
  'glosario_tabla',
  'geometry_canvas',
  'formula_destacada',
  'math_equation',
];

/** Modos para guías de aprendizaje — humanidades (lenguaje / historia). */
export const GUIDE_BLOCK_MODES_HUMANITIES: GameMode[] = [
  ...STUDY_GUIDE_THEORY_MODES,
  'structural_breakdown',
  'source_analysis',
  'completar',
  'order',
  'desarrollo',
  'sandbox',
];

/** Modos para guías de aprendizaje — genéricas (sin música). */
export const GUIDE_BLOCK_MODES_GENERIC: GameMode[] = [
  ...GUIDE_BLOCK_MODES_HUMANITIES,
  'cartesian_plane',
  'data_chart',
  'fraction_model',
  'percent_grid',
  'vincula',
  'agrupa',
];

/** Modos para guías de aprendizaje — música (incluye pentagrama y taller rítmico). */
export const GUIDE_BLOCK_MODES_MUSICA: GameMode[] = [
  'info_block',
  'canvas_breakdown',
  'rhythm_canvas',
  'symbol_analysis',
  'completar',
  'order',
  'anatomia',
  'melody_staff',
  'vincula',
  'agrupa',
  'ear_training',
  'desarrollo',
];

/** @deprecated Usa guideBlockModesForSubject() */
export const GUIDE_BLOCK_MODES: GameMode[] = GUIDE_BLOCK_MODES_MUSICA;

export function guideBlockModesForSubject(subjectTag?: string): GameMode[] {
  const tag = String(subjectTag || '').toLowerCase();
  if (tag === 'musica') return GUIDE_BLOCK_MODES_MUSICA;
  if (tag === 'historia' || tag === 'lenguaje') return GUIDE_BLOCK_MODES_HUMANITIES;
  return GUIDE_BLOCK_MODES_GENERIC;
}

/** Modos priorizados para clase interactiva / proyector — visual e interactivo. */
export const PRESENTATION_BLOCK_MODES_GENERIC: GameMode[] = [
  'data_chart',
  'geometry_canvas',
  'cartesian_plane',
  'fraction_model',
  'percent_grid',
  'number_line',
  'counting_collection',
  'math_equation',
  'sandbox',
  'arena',
  'poll',
  'completar',
  'order',
  'vincula',
  'agrupa',
  'info_block',
];

export const PRESENTATION_BLOCK_MODES_MUSICA: GameMode[] = [
  'rhythm_canvas',
  'melody_staff',
  'symbol_analysis',
  'ear_training',
  'anatomia',
  'arena',
  'poll',
  'order',
  'completar',
  'info_block',
];

export const PRESENTATION_BLOCK_MODES_HUMANITIES: GameMode[] = [
  'source_analysis',
  'structural_breakdown',
  'sandbox',
  'arena',
  'poll',
  'completar',
  'order',
  'desarrollo',
  'info_block',
];

export function presentationBlockModesForSubject(subjectTag?: string): GameMode[] {
  const tag = String(subjectTag || '').toLowerCase();
  if (tag === 'musica') return PRESENTATION_BLOCK_MODES_MUSICA;
  if (tag === 'historia' || tag === 'lenguaje') return PRESENTATION_BLOCK_MODES_HUMANITIES;
  return PRESENTATION_BLOCK_MODES_GENERIC;
}

export const EXAM_BLOCK_LABELS: Partial<Record<GameMode, string>> = {
  info_block: 'Contexto / Enseñanza',
  subtitulo: 'Sección',
  destacado: 'Destacado',
  figura: 'Recurso visual',
  glosario_tabla: 'Glosario',
  formula_destacada: 'Fórmula',
  canvas_breakdown: 'Demostración guiada',
  arena: 'Cuestionario',
  order: 'Secuencias',
  point: 'Ubica',
  sandbox: 'Explora / Simulador',
  completar: 'Completar',
  desarrollo: 'Desarrollo corto',
  vincula: 'Vincula',
  agrupa: 'Agrupa',
  dictado: 'Dictado rítmico',
  anatomia: 'Anatomía rítmica',
  rhythm_canvas: 'Pista rítmica',
  melody_staff: 'Pentagrama',
  ear_training: 'Entrenamiento auditivo',
  symbol_analysis: 'Análisis de símbolos',
  geometry_canvas: 'Plano geométrico',
  cartesian_plane: 'Plano cartesiano',
  math_equation: 'Motor de ecuaciones',
  number_line: 'Recta numérica',
  counting_collection: 'Colección contable',
  data_chart: 'Gráfico / Tabla',
  fraction_model: 'Modelo de fracción',
  percent_grid: 'Grilla de porcentaje',
  structural_breakdown: 'Desglose estructural',
  source_analysis: 'Análisis de fuente',
  poll: 'Respuesta rápida',
  coop: 'Tablero',
  wall: 'Debate',
};
